import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'

export type UserRole = 'ultramaster' | 'admin' | 'operator' | 'financial'

export interface Profile {
  id: string
  email: string
  name: string
  role: UserRole
  parking_lot_id: string | null
  has_completed_onboarding: boolean
}

export interface UserLot {
  parking_lot_id: string
  parking_lots: { id: string; name: string; address: string }
}

interface AuthContextType {
  user: User | null
  profile: Profile | null
  session: Session | null
  userLots: UserLot[]
  switchLot: (lotId: string) => Promise<void>
  completeOnboarding: () => Promise<void>
  signUp: (
    email: string,
    password: string,
    name: string,
  ) => Promise<{ error: any }>
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signOut: () => Promise<{ error: any }>
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [userLots, setUserLots] = useState<UserLot[]>([])
  const [loading, setLoading] = useState(true)

  const fetchProfileData = (sessionUser: User) => {
    supabase
      .from('profiles')
      .select('*')
      .eq('id', sessionUser.id)
      .single()
      .then(({ data }) => {
        setProfile(data as Profile)
        if (data) {
          supabase
            .from('user_parking_lots')
            .select('parking_lot_id, parking_lots(id, name, address)')
            .eq('user_id', sessionUser.id)
            .then(({ data: lots }) => {
              if (lots) setUserLots(lots as any)
              setLoading(false)
            })
        } else {
          setLoading(false)
        }
      })
  }

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (!session?.user) {
        setProfile(null)
        setUserLots([])
        setLoading(false)
      } else {
        fetchProfileData(session.user)
      }
    })

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (!session?.user) {
        setLoading(false)
      } else {
        fetchProfileData(session.user)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const switchLot = async (lotId: string) => {
    if (!profile) return
    const { error } = await supabase
      .from('profiles')
      .update({ parking_lot_id: lotId })
      .eq('id', profile.id)
    if (!error) {
      setProfile({ ...profile, parking_lot_id: lotId })
    }
  }

  const completeOnboarding = async () => {
    if (!profile) return
    const { error } = await supabase
      .from('profiles')
      .update({ has_completed_onboarding: true })
      .eq('id', profile.id)
    if (!error) {
      setProfile({ ...profile, has_completed_onboarding: true })
    }
  }

  const signUp = async (email: string, password: string, name: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name },
        emailRedirectTo: `${window.location.origin}/`,
      },
    })
    return { error }
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        userLots,
        switchLot,
        completeOnboarding,
        signUp,
        signIn,
        signOut,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
